import React, { useState, useEffect, useRef } from 'react';
import { useDS5Controller } from '../hooks/useDS5Controller';
import { useMovementCache } from '../hooks/useMovementCache';
import { api } from '../services/api';
import './SinglePassStation.css';
import packageJson from '../../package.json'; 
import Cadance from './Cadance';
const APP_VERSION = packageJson.version;

const SinglePassStation = ({ 
  userInfo,
  userPosition, 
  stationName, 
  nextJobOnPass 
}) => {
  const {
    serialNumber,
    isConnected
  } = useDS5Controller();

  const movementsCache = useMovementCache();

  const [warning, setWarning] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [hourcount,setHourCount] = useState(0);
  const [nextJob, setNextJob] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [currentMovementData, setCurrentMovementData] = useState(null);
  const [hasBeenProcessed, setHasBeenProcessed] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');
  
  const isCheckingRef = useRef(false);

  const isCorrectStation = userPosition === stationName;
     useEffect(() => {
      const resetHourlyCount = () => {
        if (typeof hourCount === 'function') return;
        setHourCount(0); // assuming setHourCount comes from parent
      };
    
      const now = new Date();
      const msUntilNextHour =
        (60 - now.getMinutes()) * 60 * 1000 -
        now.getSeconds() * 1000 -
        now.getMilliseconds();
    
      // Reset at the start of the next hour
      const timeoutId = setTimeout(() => {
        resetHourlyCount();
    
        // Then reset every hour
        const intervalId = setInterval(resetHourlyCount, 60 * 60 * 1000);
        // cleanup interval on unmount
        return () => clearInterval(intervalId);
      }, msUntilNextHour);
    
      return () => clearTimeout(timeoutId);
    }, [setHourCount]);
  useEffect(() => {
    console.log('SinglePassStation - État complet:', {
      userInfo,
      userPosition,
      isCorrectStation,
      isConnected,
      serialNumber,
      movementsCache: movementsCache ? Object.keys(movementsCache) : 'empty',
      hasBeenProcessed
    });

    
    setDebugInfo(`
      Station: ${userPosition}
      Connecté: ${isConnected}
      Serial: ${serialNumber}
      Cache: ${movementsCache ? Object.keys(movementsCache).length + ' mouvements' : 'loading'}
      Utilisateur: ${userInfo ? userInfo.user : 'none'}
      Traité: ${hasBeenProcessed ? 'OUI' : 'NON'}
    `);
  }, [userInfo, userPosition, isConnected, serialNumber, movementsCache, hasBeenProcessed]);

  const getMovementNameById = (movementId) => {
    if (!movementsCache || Object.keys(movementsCache).length === 0) return null;
    
    for (const [name, id] of Object.entries(movementsCache)) {
      if (id === movementId) return name;
    }
    return null;
  };

  useEffect(() => {
    if (!isConnected || !serialNumber || serialNumber.includes('Error')) {
      console.log('En attente de connexion...');
      setWarning('');
      setCurrentMovementData(null);
      setHasBeenProcessed(false);
      return;
    }

    if (!movementsCache || Object.keys(movementsCache).length === 0) {
      console.log('En attente du cache des mouvements...');
      return;
    }

    const checkMovementHistory = async () => {
      if (isCheckingRef.current) return;
      
      isCheckingRef.current = true;
      try {
        console.log(`Vérification historique pour: ${serialNumber}`);
        const data = await api.getCurrentMovement(serialNumber);
        
        console.log('Réponse API historique:', data);

        if (!data) {
          setWarning("Controller not in the database - Start with Quick Drift");
          setHasBeenProcessed(false);
          return;
        }

        const movementName = getMovementNameById(data.id_movement);
        const nextJobName = getMovementNameById(data.id_nextjob);

        const processedData = {
          ...data,
          movement: movementName,
          nextjob: nextJobName,
          id_movement: data.id_movement,
          id_nextjob: data.id_nextjob
        };

        setCurrentMovementData(processedData);
        
        console.log('Données traitées:', processedData);

        if (processedData.nextjob !== stationName) {
          setWarning(`Next_Job : ${processedData.nextjob || 'Aucun'}`);
          setHasBeenProcessed(false);
          return;
        }

        setWarning('');
        console.log(`Manette valide pour ${stationName}`);
        
      } catch (err) {
        console.error("Erreur vérification historique:", err);
        if (err.message.includes('404')) {
          setWarning("Controller not in database - Start with Quick Drift");
        } else {
          setWarning("Erreur de vérification de l'historique");
        }
        setHasBeenProcessed(false);
      } finally {
        isCheckingRef.current = false;
      }
    };

    checkMovementHistory();
  }, [isConnected, serialNumber, movementsCache, stationName]);

  const saveToDatabase = async () => {
    if (!userInfo || !userInfo.id) {
      setWarning("Aucun opérateur connecté. Veuillez vous authentifier.");
      return;
    }

    if (!currentMovementData || !movementsCache || Object.keys(movementsCache).length === 0) {
      setWarning("Données manquantes pour l'enregistrement");
      return;
    }

    const nextJobTarget = nextJobOnPass;
    const movementId = movementsCache[stationName];
    const nextJobId = movementsCache[nextJobTarget];

    console.log('Détails du cache:', {
      stationName,
      movementId,
      nextJobTarget, 
      nextJobId,
      allCacheKeys: Object.keys(movementsCache)
    });

    if (!movementId || !nextJobId) {
      console.error(`IDs non trouvés dans le cache: ${stationName}=${movementId}, ${nextJobTarget}=${nextJobId}`);

      const availableStations = Object.keys(movementsCache).join(', ');
      setWarning(`Station "${nextJobTarget}" non trouvée. Stations disponibles: ${availableStations}`);
      return;
    }

    const payload = {
      serial_number: serialNumber,
      operator: userInfo.id,
      tester: stationName,
      movement: movementId,
      nextjob: nextJobId,
      Grade: null,
      version: APP_VERSION
    };

    console.log("Payload envoyé à l'API:", payload);

    try {
      setIsSaving(true);
      await api.saveMovement(payload);
      setHourCount(prev => prev + 1)
      setNextJob(nextJobTarget);
      setShowSuccess(true);
      setHasBeenProcessed(true);
      
      console.log('Enregistrement réussi - Message permanent');
      
    } catch (err) {
      console.error("Erreur sauvegarde:", err);
      setWarning(`Erreur enregistrement: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    setShowSuccess(false);
    setNextJob('');
    setCurrentMovementData(null);
    setWarning('');
    setHasBeenProcessed(false);
  }, [serialNumber]);

  return (
    <div className="singlepass-container">
      <div>
        <Cadance userInfo={userInfo} movementId={stationName==='Assemblage DS5' ? 13 : 11} hourCount={hourcount} />
        <div className="singlepass-header">
          <h1>{stationName}</h1>
        </div>

        {!isConnected && (
          <div className="connection-section">
            <div className="disconnected">
              <h1>🔌 Plug in your DualSense controller.</h1>
            </div>
          </div>
        )}

        {isConnected && (
          <div className="connected-section">
            <div className="serial-display">
              <label>Numéro de Série:</label>
              <div className="serial-value">{serialNumber}</div>
            </div>
            {warning && (
              <div className="message warning-message">
                {warning}
              </div>
            )}

            {isSaving && (
              <div className="message waiting-message">
                Enregistrement en cours...
              </div>
            )}

            {showSuccess && nextJob && (
              <div className="message success-message permanent">
                <div className="success-details">
                  <div><strong>Next Job:</strong> <span className="next-job">{nextJob}</span></div>
                </div>
              </div>
            )}

            {!hasBeenProcessed && currentMovementData && currentMovementData.nextjob === stationName && 
             !isSaving && !showSuccess && userInfo && (
              <div className="action-section">
                <div className="single-button-row">
                  <button 
                    onClick={saveToDatabase}
                    disabled={isSaving}
                    className="btn-pass"
                  >
                    {isSaving ? 'Enregistrement...' : 'PASS'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SinglePassStation;